import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { motion } from "framer-motion";
import {
    DownloadSimple,
    Certificate as CertIcon,
    CircleNotch,
    EnvelopeSimple,
    PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadPNG, downloadSVG } from "@/lib/download";
import { buildCoaterzCertificateSVG } from "@/lib/coaterzCertificate";
import { getTemplate } from "@/lib/certificateTemplates";
import { getTierConfig } from "@/components/dashboard/FoundingBadge";
import { api } from "@/lib/api";

const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const toRoman = (num) => {
    const map = [
        [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
        [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
        [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
    ];
    let n = parseInt(num, 10) || 0;
    let out = "";
    for (const [v, s] of map) {
        while (n >= v) { out += s; n -= v; }
    }
    return out;
};

const svgToNode = (svgStr) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgStr, "image/svg+xml");
    return doc.documentElement;
};

// Convert an SVG element (with inlined data-URIs for images) into a PNG
// base64 string. Because the SVG has no cross-origin resources, the canvas
// is not tainted and toDataURL works. Returns raw base64 (no data: prefix).
const svgToPngBase64 = async (svgEl, { width = 850, height = 1100, scale = 2 } = {}) => {
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    try {
        const img = new Image();
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = svgUrl;
        });
        const canvas = document.createElement("canvas");
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/png");
        return dataUrl.split(",", 2)[1];
    } finally {
        URL.revokeObjectURL(svgUrl);
    }
};

/**
 * MemberCertificate — personalized Founding-tier certificate keepsake.
 * Personalization is sourced from the SSO/OAuth-authenticated user object
 * (display_name, member_number, founder_tier, member_since) that the
 * Dashboard already loads from /api/user/impact.
 *
 * Certificate assets (EVIQ logo + Impact Partner badge) are fetched as
 * base64 data URIs from /api/assets/cert-images so the SVG has no
 * cross-origin resources — the canvas stays clean and PNG download works.
 */
export const MemberCertificate = ({ user }) => {
    const [previewSvg, setPreviewSvg] = useState("");
    const [assets, setAssets] = useState(null); // { eviq_logo, impact_badge } as data URIs
    const [busyPng, setBusyPng] = useState(false);
    const [busySvg, setBusySvg] = useState(false);
    const [busyEmail, setBusyEmail] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailNote, setEmailNote] = useState("");

    const cfg = getTierConfig(user?.founder_tier);
    const memberPadded = String(user?.member_number || 0).padStart(4, "0");
    const joined = user?.member_since ? new Date(user.member_since) : new Date();
    const issuedYearRoman = useMemo(() => toRoman(joined.getFullYear()), [joined]);
    const dateLine = `Issued this ${MONTHS[joined.getMonth()]}, ${issuedYearRoman}`;

    // Preload the certificate image assets as data URIs (bypasses CDN CORS)
    useEffect(() => {
        let ignore = false;
        api.get("/assets/cert-images")
            .then((r) => { if (!ignore) setAssets(r.data); })
            .catch((e) => {
                console.warn("cert-images load failed, falling back to remote URLs", e);
                if (!ignore) setAssets({}); // fall back: builder uses remote defaults
            });
        return () => { ignore = true; };
    }, []);

    const templateId = useMemo(() => {
        if (user?.founder_tier === "founding_100") return "founding_member";
        if (user?.founder_tier === "pioneer_1000") return "lifetime_patron";
        return "founding_member";
    }, [user?.founder_tier]);

    const buildOptions = useMemo(() => {
        const tpl = getTemplate(templateId);
        return {
            recipient: user?.display_name || "EVIQ Member",
            subline: cfg.label,
            year: issuedYearRoman,
            dateLine,
            serial: `No. ${memberPadded} · ${cfg.short.toUpperCase()}`,
            signatoryName: "EVIQ Earth Initiative",
            signatoryTitle: "Founding Council",
            titleTop: tpl.titleTop,
            titleBottom: tpl.titleBottom,
            body: tpl.body,
            italicFooter: tpl.italicFooter,
            medallionLabel: tpl.medallionLabel || cfg.short.toUpperCase(),
            ...(assets?.eviq_logo ? { logoUrl: assets.eviq_logo } : {}),
            ...(assets?.impact_badge ? { badgeUrl: assets.impact_badge } : {}),
        };
    }, [
        templateId,
        user?.display_name,
        cfg.label,
        cfg.short,
        issuedYearRoman,
        dateLine,
        memberPadded,
        assets,
    ]);

    useEffect(() => {
        let ignore = false;
        (async () => {
            const qr = QRCode.create(
                `https://eviq.pro/app/impact/founder-${memberPadded}`,
                { errorCorrectionLevel: "M" }
            );
            const svg = buildCoaterzCertificateSVG({
                qrModules: qr.modules,
                ...buildOptions,
            });
            if (!ignore) setPreviewSvg(svg);
        })();
        return () => { ignore = true; };
    }, [buildOptions, memberPadded]);

    const buildFresh = () => {
        const qr = QRCode.create(
            `https://eviq.pro/app/impact/founder-${memberPadded}`,
            { errorCorrectionLevel: "M" }
        );
        return buildCoaterzCertificateSVG({
            qrModules: qr.modules,
            ...buildOptions,
        });
    };

    const filename = (ext) => {
        const clean = (user?.display_name || "member")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
        return `eviq-${clean}-${memberPadded}-certificate.${ext}`;
    };

    const handlePng = async () => {
        if (busyPng) return;
        setBusyPng(true);
        try {
            const svgEl = svgToNode(buildFresh());
            await downloadPNG(svgEl, filename("png"), { width: 850, height: 1100, scale: 3 });
            toast.success("Certificate saved", {
                description: "2550×3300 PNG · print-ready at letter/300 DPI",
            });
        } catch (e) {
            console.error(e);
            toast.error("Could not build certificate", { description: e?.message || "" });
        } finally {
            setBusyPng(false);
        }
    };

    const handleSvg = async () => {
        if (busySvg) return;
        setBusySvg(true);
        try {
            const svgEl = svgToNode(buildFresh());
            downloadSVG(svgEl, filename("svg"));
            toast.success("SVG saved — vector, infinite scale");
        } catch (e) {
            console.error(e);
            toast.error("Could not build certificate", { description: e?.message || "" });
        } finally {
            setBusySvg(false);
        }
    };

    const handleEmail = async () => {
        if (busyEmail) return;
        if (!emailTo || !/.+@.+\..+/.test(emailTo)) {
            toast.error("Enter a valid email address");
            return;
        }
        setBusyEmail(true);
        try {
            const svgEl = svgToNode(buildFresh());
            const pngBase64 = await svgToPngBase64(svgEl, { width: 850, height: 1100, scale: 2 });
            const r = await api.post("/certificates/send-member", {
                user_id: user.user_id,
                recipient_email: emailTo,
                png_base64: pngBase64,
                note: emailNote || null,
            });
            if (r.data?.ok) {
                toast.success("Certificate emailed", { description: `Delivered to ${emailTo}` });
                setEmailTo("");
                setEmailNote("");
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Something went wrong";
            toast.error("Could not send certificate", { description: detail });
        } finally {
            setBusyEmail(false);
        }
    };

    if (!user) return null;

    return (
        <motion.div
            data-testid="member-certificate-panel"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 grid grid-cols-1 gap-6 rounded-lg border border-[#1f1f22] bg-[#050505]/60 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-8"
        >
            <div className="flex justify-center">
                <div
                    data-testid="member-cert-preview"
                    className="w-full max-w-[380px] overflow-hidden rounded-md border border-[#1f1f22] shadow-[0_20px_60px_-20px_rgba(163,230,53,0.35)] [&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
                    dangerouslySetInnerHTML={{ __html: previewSvg }}
                />
            </div>

            <div className="flex flex-col justify-center gap-3">
                <div>
                    <span
                        className="font-mono-metric text-[10px] uppercase tracking-[0.24em]"
                        style={{ color: cfg.color }}
                    >
                        Your certificate
                    </span>
                    <h3 className="mt-1 font-editorial text-xl text-white sm:text-2xl">
                        Personalized to your sign-in.
                    </h3>
                    <p className="mt-2 text-xs text-zinc-500">
                        Auto-filled from your EVIQ / Tesla-linked profile: name,
                        member number, tier, and join date. Print at letter (8.5″ × 11″).
                    </p>
                </div>

                <div className="mt-2 flex flex-col gap-2 border-t border-[#1f1f22] pt-4 text-[11px] text-zinc-500 font-mono-metric uppercase tracking-[0.18em]">
                    <div className="flex items-center justify-between">
                        <span>Recipient</span>
                        <span className="text-zinc-200 normal-case tracking-normal">
                            {user.display_name}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span>Member</span>
                        <span className="text-zinc-200">#{memberPadded}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span>Tier</span>
                        <span style={{ color: cfg.color }}>{cfg.short}</span>
                    </div>
                </div>

                <button
                    type="button"
                    data-testid="member-cert-download-png"
                    onClick={handlePng}
                    disabled={busyPng || !assets}
                    className="mt-3 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                    style={{ background: cfg.color }}
                >
                    {busyPng ? (
                        <CircleNotch size={14} className="animate-spin" />
                    ) : (
                        <DownloadSimple size={14} weight="bold" />
                    )}
                    Download PNG · Print-ready
                </button>
                <button
                    type="button"
                    data-testid="member-cert-download-svg"
                    onClick={handleSvg}
                    disabled={busySvg}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#27272a] bg-black px-5 py-3 text-sm text-zinc-200 hover:border-[#3a3a3f] disabled:opacity-60"
                >
                    {busySvg ? (
                        <CircleNotch size={14} className="animate-spin" />
                    ) : (
                        <CertIcon size={14} weight="fill" />
                    )}
                    Download SVG · Vector
                </button>

                <div className="mt-3 flex flex-col gap-2 border-t border-[#1f1f22] pt-4">
                    <div className="flex items-center gap-2 text-zinc-500">
                        <EnvelopeSimple size={13} weight="fill" color={cfg.color} />
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                            Email my certificate
                        </span>
                    </div>
                    <input
                        data-testid="member-cert-email-to"
                        type="email"
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-md border border-[#27272a] bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none"
                    />
                    <textarea
                        data-testid="member-cert-email-note"
                        value={emailNote}
                        onChange={(e) => setEmailNote(e.target.value)}
                        rows={2}
                        placeholder="Optional note…"
                        className="w-full resize-none rounded-md border border-[#27272a] bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none"
                    />
                    <button
                        type="button"
                        data-testid="member-cert-email-send"
                        onClick={handleEmail}
                        disabled={busyEmail || !assets}
                        className="inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                        style={{ borderColor: cfg.color, color: cfg.color, background: "rgba(163,230,53,0.05)" }}
                    >
                        {busyEmail ? (
                            <CircleNotch size={14} className="animate-spin" />
                        ) : (
                            <PaperPlaneTilt size={14} weight="bold" />
                        )}
                        Send to me / a friend
                    </button>
                    <p className="text-[10px] leading-relaxed text-zinc-600">
                        Sent via Resend. Delivers as a PNG attachment.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};

export default MemberCertificate;
